import { useState, useEffect } from "react";
import type { ProductData } from "@/mocks/products";

interface Props {
  product: ProductData;
}

export default function SecretDoor({ product }: Props) {
  const [phase, setPhase] = useState<"idle" | "awakening" | "opening" | "open">("idle");
  const [glyphVisible, setGlyphVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setGlyphVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  const handleActivate = () => {
    if (phase !== "idle") return;
    setPhase("awakening");
    setTimeout(() => setPhase("opening"), 900);
    setTimeout(() => setPhase("open"), 1800);
  };

  const handleClose = () => {
    setPhase("idle");
  };

  const [r, g, b] = hexToRgb(product.accentColor);

  return (
    <div className="flex flex-col items-center justify-center">
      {/* Trigger — hidden glyph */}
      <div
        className="relative flex flex-col items-center cursor-pointer group"
        onClick={handleActivate}
        style={{ opacity: glyphVisible ? 1 : 0, transition: "opacity 2s ease" }}
      >
        {/* Outer ring */}
        <div
          className="relative w-20 h-20 flex items-center justify-center"
          style={{
            transition: "transform 1s ease",
            transform: phase === "awakening" ? "rotate(180deg) scale(1.2)" : "rotate(0deg) scale(1)",
          }}
        >
          {/* Pulsing ring */}
          <div
            className="absolute inset-0 rounded-full animate-pulse"
            style={{
              border: `1px solid rgba(${r},${g},${b},0.15)`,
              animationDuration: "3s",
            }}
          />
          {/* Rotating ring on hover */}
          <div
            className="absolute inset-[-6px] rounded-full"
            style={{
              border: `1px solid rgba(${r},${g},${b},0.08)`,
              borderTopColor: `rgba(${r},${g},${b},0.4)`,
              transition: "transform 1.2s ease, opacity 0.8s ease",
              transform: phase === "awakening" || phase === "opening" ? "rotate(360deg)" : "rotate(0deg)",
              opacity: phase === "idle" ? 0.4 : 1,
            }}
          />

          {/* Center symbol */}
          <div
            className="relative z-10 flex items-center justify-center w-12 h-12"
            style={{
              transition: "all 0.8s ease",
              filter: phase !== "idle"
                ? `drop-shadow(0 0 12px rgba(${r},${g},${b},0.8))`
                : "none",
            }}
          >
            <span
              className="select-none"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.6rem",
                color: phase !== "idle"
                  ? `rgba(${r},${g},${b},1)`
                  : `rgba(${r},${g},${b},0.35)`,
                transition: "color 0.8s ease",
                letterSpacing: "0",
              }}
            >
              ✦
            </span>
          </div>
        </div>

        {/* Label */}
        <div
          className="mt-3 text-center"
          style={{
            opacity: phase === "idle" ? 1 : 0,
            transition: "opacity 0.5s ease",
          }}
        >
          <span
            className="text-xs tracking-[0.4em] uppercase"
            style={{
              color: `rgba(${r},${g},${b},0.3)`,
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: "0.4em",
            }}
          >
            購入する
          </span>
          <div
            className="mt-1 w-4 h-px mx-auto"
            style={{ background: `rgba(${r},${g},${b},0.2)` }}
          />
        </div>
      </div>

      {/* Overlay portal */}
      {(phase === "opening" || phase === "open") && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            background: "rgba(6,5,8,0.95)",
            opacity: phase === "open" ? 1 : 0,
            transition: "opacity 0.8s ease",
            backdropFilter: "blur(12px)",
          }}
          onClick={handleClose}
        >
          {/* Sacred geometry rings */}
          {[120, 200, 300, 420].map((size, i) => (
            <div
              key={i}
              className="absolute rounded-full pointer-events-none"
              style={{
                width: size,
                height: size,
                border: `1px solid rgba(${r},${g},${b},${0.12 - i * 0.02})`,
                animation: `spin ${8 + i * 4}s linear infinite ${i % 2 === 0 ? "" : "reverse"}`,
                opacity: phase === "open" ? 1 : 0,
                transition: `opacity 0.6s ease ${i * 0.1}s`,
              }}
            />
          ))}

          {/* Center glow */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 500,
              height: 500,
              background: `radial-gradient(circle, rgba(${r},${g},${b},0.08) 0%, transparent 70%)`,
              filter: "blur(40px)",
            }}
          />

          {/* Content panel */}
          <div
            className="relative z-10 text-center px-8 py-12 max-w-sm w-full"
            style={{
              opacity: phase === "open" ? 1 : 0,
              transform: phase === "open" ? "translateY(0) scale(1)" : "translateY(16px) scale(0.97)",
              transition: "all 0.9s ease 0.3s",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top ornament */}
            <div className="flex items-center justify-center gap-3 mb-10">
              <div className="w-10 h-px" style={{ background: `rgba(${r},${g},${b},0.2)` }} />
              <span
                className="text-xs tracking-[0.5em] uppercase"
                style={{ color: `rgba(${r},${g},${b},0.4)`, fontFamily: "'Cormorant Garamond', serif" }}
              >
                Sacred Gateway
              </span>
              <div className="w-10 h-px" style={{ background: `rgba(${r},${g},${b},0.2)` }} />
            </div>

            {/* Product name */}
            <h3
              className="text-4xl font-light text-white/80 mb-2"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.08em" }}
            >
              {product.name}
            </h3>
            <p
              className="text-sm mb-1"
              style={{
                color: `rgba(${r},${g},${b},0.5)`,
                fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: "0.2em",
              }}
            >
              {product.subtitle}
            </p>

            {/* Price */}
            <p
              className="text-white/20 text-xs tracking-[0.3em] mb-10 mt-2"
            >
              {product.details.price}
            </p>

            {/* Divider */}
            <div
              className="w-24 h-px mx-auto mb-10"
              style={{ background: `rgba(${r},${g},${b},0.2)` }}
            />

            {/* Message */}
            <p
              className="text-white/30 text-sm font-light leading-relaxed mb-10"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.05em" }}
            >
              扉が開かれた。<br />
              あなたを待っていた。
            </p>

            {/* Primary CTA — Square link */}
            <a
              href={product.ctaShopifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block cursor-pointer whitespace-nowrap mb-5"
              style={{
                border: `1px solid rgba(${r},${g},${b},0.35)`,
                padding: "16px 0",
                transition: "all 0.7s ease",
                background: `rgba(${r},${g},${b},0.05)`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = `rgba(${r},${g},${b},0.7)`;
                (e.currentTarget as HTMLElement).style.background = `rgba(${r},${g},${b},0.12)`;
                (e.currentTarget as HTMLElement).style.boxShadow = `0 0 32px rgba(${r},${g},${b},0.2)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = `rgba(${r},${g},${b},0.35)`;
                (e.currentTarget as HTMLElement).style.background = `rgba(${r},${g},${b},0.05)`;
                (e.currentTarget as HTMLElement).style.boxShadow = "none";
              }}
            >
              <span
                className="text-xs tracking-[0.45em] uppercase"
                style={{ color: `rgba(${r},${g},${b},0.8)`, letterSpacing: "0.45em" }}
              >
                購入する
              </span>
            </a>

            {/* Close */}
            <button
              onClick={handleClose}
              className="cursor-pointer whitespace-nowrap"
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.15)",
                fontSize: "0.65rem",
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                transition: "color 0.4s ease",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.35)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.15)";
              }}
            >
              ← 戻る
            </button>
          </div>
        </div>
      )}

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [201, 168, 76];
}
